import { useMemo, useCallback } from "react"

import { Feature } from "@turf/turf"
import { merge } from "lodash"
import { useMatch } from "react-router-dom"

import {
    Layer,
    L2RLayerProps,
    MapStyles,
    LINE_LAYER_SIZE,
    SELECTED_LINE_LAYER_SIZE,
    getLineLayerConfig,
    getInterpolatedLineLayerWidth,
    getInterpolatedSelectableLineLayerWidth,
    getSelectableLineSortKey,
    REFERENTIAL_SOURCE,
    REFERENTIAL_LAYER,
    REFERENTIAL_LAYER_CLICKABLE,
    useOrderingLayers,
    BASE_MAP_ID,
    useMapStateContext,
} from "@l2r-front/l2r-map"
import { useTheme } from "@l2r-front/l2r-ui"
import { selectLayerColorOpacity } from "@l2r-front/l2r-utils"

import { NetworkReferentialSource } from "../NetworkReferentialSource"

export type BaseNetworkReferentialLayerProps = {
    interactive?: boolean,
    onClickOnFeature?: (feature: Feature) => void,
    mapId?: string,
    mapStyle?: object,
    roadToHide?: string,
    selectedRoad?: string,
    selectedRoadColor?: string,
    subNetworks?: string[] | null,
} & Omit<L2RLayerProps, "id" | "type" | "render">

export function BaseNetworkReferentialLayer(props: BaseNetworkReferentialLayerProps) {

    const {
        onClickOnFeature,
        mapId = BASE_MAP_ID,
        mapStyle,
        roadToHide,
        selectedRoad = null,
        selectedRoadColor,
        subNetworks = null,
        ...layerProps
    } = props

    const { interactive = true } = layerProps

    const theme = useTheme()
    const matchRoad = useMatch(":network/:app/:module/:road/*")

    const isRoadView = useMemo(() => {
        return matchRoad?.params?.app !== "campaign" && matchRoad?.params?.road
    }, [matchRoad])

    useOrderingLayers([REFERENTIAL_LAYER_CLICKABLE, REFERENTIAL_LAYER], mapId)
    const { displayReferential } = useMapStateContext()
    const referentialLayerConfig = useMemo(() => {
        const shouldHideReferential = !displayReferential && !isRoadView && mapId === BASE_MAP_ID
        const lineColor = shouldHideReferential ? "transparent"
            : mapStyle === MapStyles.plan ? selectLayerColorOpacity(theme.palette.map.darkReferential, 0.5, selectedRoad, selectedRoadColor)
                : selectLayerColorOpacity(theme.palette.map.lightReferential, 0.95, selectedRoad, selectedRoadColor)
        return merge(
            getLineLayerConfig({
                paint: {
                    "line-color": lineColor,
                    "line-width": getInterpolatedLineLayerWidth(LINE_LAYER_SIZE),
                },
            }),
            {
                paint: {
                    "line-color": lineColor,
                    "line-width": getInterpolatedSelectableLineLayerWidth(selectedRoad),
                },
                layout: {
                    "line-sort-key": getSelectableLineSortKey(selectedRoad),
                },
                filter: ["!=", ["get", "road", ["at", 0, ["get", "linearLocation"]]], roadToHide || null],
            },
        )
    }, [displayReferential, mapId, mapStyle, isRoadView, selectedRoad, selectedRoadColor, theme.palette.map.darkReferential, theme.palette.map.lightReferential, roadToHide])

    const referentialLayerClickConfig = useMemo(() => {
        return getLineLayerConfig({
            "paint": {
                "line-color": "transparent",
                "line-width": getInterpolatedLineLayerWidth(SELECTED_LINE_LAYER_SIZE),
            },
        })
    }, [])

    const handleLayerClick = useCallback(
        async (event: { feature?: Feature }) => {
            const feature = event?.feature
            if (!feature) {
                return
            } else {
                if (onClickOnFeature) {
                    onClickOnFeature(feature)
                }
            }
        },
        [onClickOnFeature]
    )

    return (
        <NetworkReferentialSource id={REFERENTIAL_SOURCE} subNetworks={subNetworks}>
            <Layer
                {...referentialLayerConfig}
                id={REFERENTIAL_LAYER}
                {...layerProps}
            />
            {interactive && <Layer
                {...referentialLayerClickConfig}
                id={REFERENTIAL_LAYER_CLICKABLE}
                onClick={handleLayerClick}
                {...layerProps}
            />}
        </NetworkReferentialSource>
    )
}
